import { isRouteErrorResponse } from '@remix-run/react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Layout } from '~/components/Layout';
import type { UserData } from '~/dataType';

interface ErrorPageProps {
  error: unknown;
  user?: UserData | null;
}

export function ErrorPage({ error, user }: ErrorPageProps) {
  let title = 'An Error Occurred';
  let message = 'An unexpected error occurred. Please try again later.';
  let statusCode: number | undefined;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    
    switch (error.status) {
      case 404:
        title = 'Page Not Found';
        message = "The page you're looking for doesn't exist or may have been moved or deleted.";
        break;
      case 401:
        title = 'Authentication Required';
        message = 'You need to be logged in to access this page.';
        break;
      case 403:
        title = 'Access Denied';
        message = "You don't have permission to access this page.";
        break;
      case 500:
        title = 'Server Error';
        message = 'A server error occurred. Please try again later.';
        break;
      default:
        title = `Error ${error.status}`;
        message = error.data || error.statusText || 'An error occurred.';
    }
  } else if (error instanceof Error) {
    title = 'Application Error';
    message = process.env.NODE_ENV === 'development' 
      ? error.message 
      : 'An unexpected error occurred. Please try again later.';
  }

  return (
    <Layout user={user}>
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex flex-col items-center gap-2">
              <div className="text-6xl font-bold text-gray-900">
                {statusCode || '!'}
              </div>
              <div className="text-xl text-gray-900">{title}</div>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600 leading-relaxed">
              {message}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button 
                onClick={() => window.history.back()}
                variant="outline"
              >
                Go Back
              </Button>
              <Button 
                onClick={() => window.location.href = '/latest'}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                Go Home
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && error instanceof Error && (
              <details className="mt-6 text-left bg-gray-100 p-3 rounded text-sm">
                <summary className="cursor-pointer font-medium text-gray-800">
                  Developer Details
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-800">
                  {error.stack}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </main>
    </Layout>
  );
}